import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { semicolonSeparatedToCommadSeparated } from '../../Utils/Format';
import MultiSelector from '../../Atoms/MultiSelector';
import { activitésEntreprise, categoriesViaListing } from '../../Utils/Listings';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 90%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-radius: 10px 10px 0 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2d3748;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #a0aec0;
  cursor: pointer;
  transition: color 0.2s, transform 0.2s;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #4a5568;
    background-color: #f7fafc;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModalBody = styled.div`
  padding: 1.75rem 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background-color: #f8fafc;
  border-radius: 0 0 10px 10px;
`;

const Button = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: translateY(1px);
  }
`;

const CancelButton = styled(Button)`
  background-color: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  
  &:hover {
    background-color: #f7fafc;
    border-color: #cbd5e0;
  }
`;

const SaveButton = styled(Button)`
  background-color: #4361ee;
  color: white;
  border: none;
  
  &:hover {
    background-color: #3a56e4;
  }
`;

const OwnerInfo = styled.div`
  background-color: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: #2c5282;
  font-weight: 600;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: #2d3748;
`;

const ActivityEditModal = ({ isOpen, onClose, companyId, profileId, activity, onSave }) => {
  // Déclarer tous les hooks au début du composant, avant toute condition
  const [selectedActivity, setSelectedActivity] = useState(activity || "");
  
  // Utiliser useEffect pour mettre à jour les données après le rendu initial
  useEffect(() => {
    if (isOpen) {
      setSelectedActivity(activity || "");
    }
  }, [isOpen, activity]);

  // Si la modal n'est pas ouverte, ne rien afficher
  if (!isOpen) return null;
  
  // Gérer le clic sur le bouton Enregistrer
  const handleSaveClick = () => {
    onSave(companyId, profileId, selectedActivity);
    onClose();
  };
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Modifier l'activité (catégorie VIA) {companyId && "de la société"}{profileId && "du profil"}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <OwnerInfo>
            <InfoTitle>Activité actuelle</InfoTitle>
            <InfoText>{activity ? semicolonSeparatedToCommadSeparated(activity) : "aucune"}</InfoText>
          </OwnerInfo>

          <MultiSelector
            onChange={setSelectedActivity}
            defaultValue={activity}
            valuesListing={companyId ? activitésEntreprise : categoriesViaListing}
            label={"Nouvelle activité"}
            name="activity"
            style={{width: "100%", marginBottom: "1.5rem"}}
          />
          
        </ModalBody>
        <ModalFooter>
          <CancelButton type="button" onClick={onClose}>
            Annuler
          </CancelButton>
          <SaveButton type="button" onClick={handleSaveClick}>
            Enregistrer
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ActivityEditModal;